import React, { createContext, useContext, useMemo } from "react";
import type { CSSProperties, PropsWithChildren } from "react";
import type {
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./style.scss";
import { HolderOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { DEFAULT_DRAGABLE_OPACITY } from "../../../../constants/app";

interface Props {
  id: UniqueIdentifier;
  className?: string;
  styles?: CSSProperties;
}

interface Context {
  attributes: Record<string, any>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
}

const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {},
});

export function SortableItem({
  children,
  id,
  className,
  styles,
}: PropsWithChildren<Props>) {
const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef]
  );
  const style: CSSProperties = {
    opacity: isDragging ? DEFAULT_DRAGABLE_OPACITY : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <SortableItemContext.Provider value={context}>
      <li
        className={`sortableItem ${className}`}
        ref={setNodeRef}
        style={{ ...style, ...styles }}
      >
        {children}
      </li>
    </SortableItemContext.Provider>
  );
}

export function DragHandle() {
  const { attributes, listeners, ref } = useContext(SortableItemContext);

  return (
    <Button
      icon={<HolderOutlined />}
      className="dragHandle"
      {...attributes}
      {...listeners}
      ref={ref}
    ></Button>
  );
}
